import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { PaymentParts } from "./VoucherBalance";
import { api, formatMoney, type BankLine, type BankLineDetail } from "./api";
import { IconAction } from "./ui";
import { BankDocumentItems, DocumentAdjustments } from "./BankDocumentItems";

/** D, 7 October 2026: read the linked record by hovering, while a click still opens the actions. */
export function BankPeek({ line, onClick }: { readonly line: BankLine; readonly onClick: () => void }): React.ReactElement {
  const [at, setAt] = useState<{ left: number; top: number; width: number; anchor: number }>();
  const [detail, setDetail] = useState<BankLineDetail>();
  const [error, setError] = useState<string>();
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const generation = useRef(0);
  const loaded = useRef<BankLineDetail | undefined>(undefined);
  const card = useRef<HTMLDivElement>(null);
  /**
   * The card shows every item (D, 14 October 2026: a split of 9,020 into many categories was
   * cut off). It cannot be scrolled, as it follows the pointer, so once it has its content it
   * is moved up as far as it needs to fit on the screen.
   */
  useLayoutEffect(() => {
    if (!at || !card.current) return;
    const height = card.current.scrollHeight;
    const top = Math.max(8, Math.min(at.anchor, window.innerHeight - height - 8));
    if (top !== at.top) setAt({ ...at, top });
  }, [at, detail, error]);
  useEffect(() => {
    generation.current++; loaded.current = undefined; setDetail(undefined);
    return () => { generation.current++; clearTimeout(timer.current); };
  }, [line.id, line.match?.id, line.match?.kind, line.isIgnored]);
  function leave(): void { clearTimeout(timer.current); setAt(undefined); }
  function enter(element: HTMLElement): void {
    clearTimeout(timer.current);
    const rect = element.getBoundingClientRect();
    timer.current = setTimeout(() => {
      const width = Math.min(620, window.innerWidth - 16);
      setAt({ width, left: Math.max(8, Math.min(rect.left - width - 12, window.innerWidth - width - 8)), top: Math.max(8, Math.min(rect.top, window.innerHeight - 320)), anchor: rect.top });
      setError(undefined);
      if (loaded.current) { setDetail(loaded.current); return; }
      const version = generation.current;
      void api.bankLine(line.id).then(({ line: got }) => {
        if (version !== generation.current) return;
        loaded.current = got; setDetail(got);
      }).catch((problem: unknown) => { if (version === generation.current) setError(problem instanceof Error ? problem.message : "Could not load the transaction"); });
    }, 250);
  }
  return <div onMouseEnter={(event) => enter(event.currentTarget)} onMouseLeave={leave} onFocus={(event) => enter(event.currentTarget)} onBlur={leave}>
    <IconAction icon={line.match || line.isIgnored ? "ri-eye-line" : "ri-links-line"} label={line.match || line.isIgnored ? "See or undo the match" : "Match this line"} tip="Hover to see details. Click to edit the match." tone={line.match || line.isIgnored ? "info" : "primary"} onClick={() => { leave(); onClick(); }} />
    {at ? <div ref={card} role="tooltip" className="fixed z-[210] p-4 rounded-md bg-white dark:bg-bodybg border border-defaultborder shadow-lg pointer-events-none overflow-hidden text-start whitespace-normal" style={{ left: at.left, top: at.top, width: at.width, maxHeight: window.innerHeight - at.top - 8 }}>
      <p className="font-semibold mb-1">{line.postDate} · {formatMoney(line.amountCents)}</p>
      <p className="mb-1">{line.counterparty ?? "Bank transaction"}</p>
      {line.payerNote ? <p className="text-[0.8125rem] mb-2">{line.payerNote}</p> : null}
      {error ? <p>{error}</p> : !detail ? <p>Loading details</p> : <>
        <p className="text-[0.8125rem]">{detail.match?.label ?? (detail.isIgnored ? detail.ignoreReason : "Unmatched")}</p>
        {detail.matchedDocument ? <>
          <p className="font-semibold">{detail.matchedDocument.number} · {detail.matchedDocument.day}</p>
          <BankDocumentItems items={detail.matchedDocument.items} expenseItems={detail.matchedDocument.expenseItems} compact />
          {/* What it looked like: the photos taken with it, and the products' own pictures (D, 11 October 2026), large enough to read (D, 15 October 2026). */}
          {/* An invoice kept as a PDF, its first page (D, 15 October 2026: it showed as a broken picture). */}
          {(detail.matchedDocument.pdfIds?.length ?? 0) > 0 ? (
            <div className="flex flex-wrap gap-2 my-2">
              {detail.matchedDocument.pdfIds?.map((id) => (
                <iframe key={id} title="Invoice" src={`/api/photos/${id}#toolbar=0&navpanes=0&view=FitH`} className="w-56 h-72 rounded-sm border border-defaultborder bg-white" />
              ))}
            </div>
          ) : null}
          {(detail.matchedDocument.photoIds?.length ?? 0) > 0 ? (
            // Whole and large enough to read a card and a payment screen (D, 19 October 2026:
            // "can you enlarge, I can't see the photo"): one photo the card's width, several two
            // to a row, none cut to a square.
            <div className={`grid gap-2 my-2 ${(detail.matchedDocument.photoIds?.length ?? 0) > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
              {detail.matchedDocument.photoIds?.map((id) => (
                <img
                  key={id}
                  src={`/api/photos/${id}?preview=1`}
                  alt=""
                  className={`w-full object-contain rounded-sm border border-defaultborder bg-light ${(detail.matchedDocument?.photoIds?.length ?? 0) > 1 ? "h-64" : "max-h-[26rem]"}`}
                />
              ))}
            </div>
          ) : null}
          <div className="text-end border-t border-defaultborder pt-2"><DocumentAdjustments subtotalCents={detail.matchedDocument.subtotalCents} discountCents={detail.matchedDocument.discountCents} surchargeCents={detail.matchedDocument.surchargeCents} /><p className="mb-1">Total S$ {formatMoney(detail.matchedDocument.totalCents)}</p>{(detail.matchedDocument.payments?.length ?? 0) > 1 ? <PaymentParts totalCents={detail.matchedDocument.totalCents} payments={detail.matchedDocument.payments ?? []} done={detail.matchedDocument.paidCents === null} /> : null}{detail.matchedDocument.paidCents !== null ? <><p className="mb-1">Paid S$ {formatMoney(detail.matchedDocument.paidCents)}</p><p className={`font-semibold mb-0 ${detail.matchedDocument.totalCents > detail.matchedDocument.paidCents ? "text-warning" : "text-success"}`}>S$ {formatMoney(Math.max(0, detail.matchedDocument.totalCents - detail.matchedDocument.paidCents))} remaining</p></> : null}</div>
        </> : <p className="text-[0.8125rem] mb-0">{line.description}</p>}
      </>}
    </div> : null}
  </div>;
}
